/**
 * Strata height fields for the merged rock mass (lifted from the shared
 * prototype engine's `engine/src/geology.js`: `strataY`, `strataSegment`,
 * `strataWarp`; the town sandstone and cave textures there are not needed by
 * the dungeon). Pure functions of world position; the rock mass samples them
 * per corner so neighbouring cells share one smooth bed height.
 */
import { presentation } from './constants.ts';

const g = presentation.medium.geology;
const turn = 2 * Math.PI;

/** Bed index `u` to height: monotonic, with uneven bed thickness. */
export const strataY = (u: number): number =>
  g.bed * u + g.swell[0] * Math.sin((u * turn) / g.swell[1]) + g.ripple[0] * Math.sin((u * turn) / g.ripple[1] + g.ripple[2]);

/** The segment a point belongs to: rock is cut into blocks along a fixed direction, and beds jog at their joints. */
const strataSegment = (x: number, z: number): number => {
  const a = x * g.segmentAxis[0] + z * g.segmentAxis[1];
  return Math.floor(a / g.segmentLength + g.segmentWobble[0] * Math.sin(a * g.segmentWobble[1]));
};

/** A smooth height offset of the beds at a point, jogging between segments. */
export const strataWarp = (x: number, z: number): number => {
  const s = strataSegment(x, z);
  let sum = 0;
  for (const [amplitude, fx, fz] of g.warp) sum += amplitude * Math.sin(x * fx + z * fz);
  for (const [amplitude, f] of g.jog) sum += amplitude * Math.sin(s * f);
  return sum;
};
